/**
 * Image cache for creators' custom character art. `getImage` returns the element once it has
 * loaded (null before that, and in environments without `Image`, such as tests), so a drawing
 * routine can fall back to the drawn mole meanwhile. Pass `onReady` to be told when it arrives.
 */
const cache = new Map<string, { img: HTMLImageElement; ready: boolean; failed: boolean }>();
const waiters = new Map<string, Set<() => void>>();

export function getImage(url: string, onReady?: () => void): HTMLImageElement | null {
  if (typeof Image === 'undefined') return null;
  let entry = cache.get(url);
  if (!entry) {
    const img = new Image();
    entry = { img, ready: false, failed: false };
    cache.set(url, entry);
    img.onload = () => {
      entry!.ready = true;
      for (const cb of waiters.get(url) ?? []) cb();
      waiters.delete(url);
    };
    img.onerror = () => {
      entry!.failed = true;
      waiters.delete(url);
    };
    img.src = url;
  }
  if (!entry.ready && !entry.failed && onReady) {
    let set = waiters.get(url);
    if (!set) waiters.set(url, (set = new Set()));
    set.add(onReady);
  }
  return entry.ready ? entry.img : null;
}
